import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  captureExternalAppEvidence,
  getExternalAppEvidenceScreenshot,
  getLatestExternalAppPromptRun,
  saveExternalAppPlaybackHealth,
} from './externalAppControlApi';
import {
  buildVisualTemplateFromImage,
  normalizedPointFromImage,
} from './visualTemplateSelection';

const DEFAULT_CONFIG = {
  enabled: false,
  detectorRuleId: '',
  bufferingDetector: null,
  continuousRuleIds: [],
  recoveryRuleId: '',
  initialDelayMs: 30000,
  healthyProbeIntervalMs: 45000,
  confirmationIntervalMs: 3000,
  requiredMatches: 2,
  screenshotBudgetPerHour: 60,
  recoveryEnabled: false,
  recoveryCooldownMs: 120000,
  maxRecoveriesPerRun: 2,
};

function PlaybackHealthPanel({
  session,
  appLabel,
  disabled,
  simple = false,
  onAddBlocker,
  onMessage,
  onError,
}) {
  const rules = session?.promptRules || session?.draftProfile?.promptRules || [];
  const visualRules = useMemo(
    () => rules.filter((rule) =>
      rule.promptType !== 'ready_screen'
      && rule.detectors?.some((detector) => detector.type === 'visual_template')
      && (!simple || rule.response?.steps?.length > 0)
    ),
    [rules]
  );
  const recoveryRules = useMemo(
    () => visualRules.filter((rule) => rule.response?.steps?.length > 0),
    [visualRules]
  );
  const [config, setConfig] = useState(() => ({
    ...DEFAULT_CONFIG,
    ...(session?.draftProfile?.playbackHealth || {}),
  }));
  const [busy, setBusy] = useState('');
  const [latestRun, setLatestRun] = useState(null);
  const [capture, setCapture] = useState(null);
  const [selection, setSelection] = useState(null);
  const [bufferingThreshold, setBufferingThreshold] = useState(0.86);
  const imageRef = useRef(null);
  const dragStartRef = useRef(null);

  useEffect(() => {
    const next = {
      ...DEFAULT_CONFIG,
      ...(session?.draftProfile?.playbackHealth || {}),
    };
    setConfig(next);
    setBufferingThreshold(next.bufferingDetector?.template?.threshold || 0.86);
    setCapture(null);
    setSelection(null);
  }, [session?.id, session?.draftProfile?.playbackHealth]);

  useEffect(() => {
    if (!session?.id || !config.enabled) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const run = await getLatestExternalAppPromptRun(session.id, 'health');
        if (!cancelled && run?.trigger === 'continuous_playback_health') {
          setLatestRun(run);
        }
      } catch (error) {
        if (!cancelled) {
          onError(error.response?.data?.error || error.message || 'Failed to load health status');
        }
      }
    };
    const timer = window.setInterval(load, 5000);
    load();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [config.enabled, onError, session?.id]);

  const update = (key, value) => {
    setConfig((current) => ({ ...current, [key]: value }));
  };

  const handleCapture = async () => {
    setBusy('capture');
    onError('');
    try {
      const evidence = await captureExternalAppEvidence(session.id, true, 3000);
      const screenshot = await getExternalAppEvidenceScreenshot(
        session.id,
        evidence.evidenceId
      );
      if (!screenshot?.dataUrl) {
        throw new Error('The device returned evidence without a screenshot.');
      }
      setCapture({ evidence, dataUrl: screenshot.dataUrl });
      setSelection(null);
      onMessage('Buffering screen captured. Draw around stable buffering text or artwork.');
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Failed to capture buffering screen');
    } finally {
      setBusy('');
    }
  };

  const pointFromEvent = (event) =>
    normalizedPointFromImage(event.clientX, event.clientY, imageRef.current);

  const handlePointerDown = (event) => {
    const point = pointFromEvent(event);
    if (!point) return;
    dragStartRef.current = point;
    setSelection({ ...point, width: 0, height: 0 });
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!dragStartRef.current) return;
    const point = pointFromEvent(event);
    if (!point) return;
    const start = dragStartRef.current;
    setSelection({
      x: Math.min(start.x, point.x),
      y: Math.min(start.y, point.y),
      width: Math.abs(point.x - start.x),
      height: Math.abs(point.y - start.y),
    });
  };

  const handlePointerUp = () => {
    dragStartRef.current = null;
    setSelection((current) => (
      current?.width >= 0.01 && current?.height >= 0.01 ? current : null
    ));
  };

  const toggleContinuousRule = (ruleId) => {
    setConfig((current) => {
      const selected = current.continuousRuleIds || [];
      return {
        ...current,
        continuousRuleIds: selected.includes(ruleId)
          ? selected.filter((entry) => entry !== ruleId)
          : [...selected, ruleId],
      };
    });
  };

  const handleSave = async () => {
    setBusy('save');
    onError('');
    try {
      let bufferingDetector = config.bufferingDetector;
      if (capture && selection) {
        bufferingDetector = {
          id: 'buffering-indicator',
          name: 'Buffering indicator',
          template: buildVisualTemplateFromImage(
            imageRef.current,
            selection,
            bufferingThreshold
          ),
        };
      } else if (bufferingDetector?.template) {
        bufferingDetector = {
          ...bufferingDetector,
          template: {
            ...bufferingDetector.template,
            threshold: bufferingThreshold,
          },
        };
      }
      const payload = {
        ...config,
        detectorRuleId: null,
        bufferingDetector,
      };
      const saved = await saveExternalAppPlaybackHealth(session.id, payload);
      setConfig({ ...DEFAULT_CONFIG, ...saved });
      setBufferingThreshold(saved.bufferingDetector?.template?.threshold || 0.86);
      setCapture(null);
      setSelection(null);
      onMessage(
        saved.enabled
          ? 'Continuous visual monitoring saved.'
          : 'Continuous visual monitoring disabled.'
      );
    } catch (error) {
      onError(
        error.response?.data?.error
        || error.response?.data?.message
        || error.message
        || 'Failed to save continuous monitoring'
      );
    } finally {
      setBusy('');
    }
  };

  const hasMonitor = Boolean(
    config.bufferingDetector
    || config.detectorRuleId
    || config.continuousRuleIds?.length
    || (capture && selection)
  );

  return (
    <div className="apps-playback-health">
      <div className="apps-playback-health__header">
        <div>
          <h4>{simple ? 'Continuous monitoring' : 'Continuous Playback Health'}</h4>
          <p>
            {simple
              ? 'After automatic startup reaches the ready screen, periodically check for selected adverts, prompts, and notices and run their saved responses.'
              : 'One sparse screenshot is shared across buffering detection and every selected saved pattern. Passive device telemetry decides when that visual probe is worthwhile.'}
          </p>
        </div>
        <label className="apps-playback-health__enable">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(event) => update('enabled', event.target.checked)}
            disabled={!hasMonitor}
          />
          Enable monitoring
        </label>
      </div>

      {!simple && <section className="apps-playback-health__section">
        <div className="apps-playback-health__section-heading">
          <div>
            <strong>Buffering detection</strong>
            <p>Capture buffering while it is visible, then select its stable logo or text.</p>
          </div>
          <button type="button" onClick={handleCapture} disabled={disabled || Boolean(busy)}>
            {busy === 'capture' ? 'Capturing…' : 'Capture Buffering Screenshot'}
          </button>
        </div>

        {capture && (
          <div className="apps-prompt-capture">
            <div className="apps-prompt-guidance">
              <strong>Select the stable buffering indicator</strong>
              <p>
                Prefer stable text or artwork. Avoid rotating spinner segments and changing video.
              </p>
            </div>
            <div
              className="apps-visual-template-selector"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              role="application"
              aria-label={`Select buffering indicator for ${appLabel}`}
            >
              <img
                ref={imageRef}
                src={capture.dataUrl}
                alt={`Current ${appLabel} buffering screen`}
                draggable="false"
              />
              {selection && (
                <span
                  className="apps-visual-template-selection"
                  style={{
                    left: `${selection.x * 100}%`,
                    top: `${selection.y * 100}%`,
                    width: `${selection.width * 100}%`,
                    height: `${selection.height * 100}%`,
                  }}
                />
              )}
            </div>
          </div>
        )}

        {(config.bufferingDetector || capture) && (
          <div className="apps-playback-health__buffering-template">
            {config.bufferingDetector?.template?.templateDataUrl && !capture && (
              <img
                src={config.bufferingDetector.template.templateDataUrl}
                alt="Saved buffering indicator"
              />
            )}
            <label>
              <span>Buffering match threshold</span>
              <input
                type="range"
                min="0.5"
                max="0.99"
                step="0.01"
                value={bufferingThreshold}
                onChange={(event) => setBufferingThreshold(Number(event.target.value))}
              />
              <small>{Math.round(bufferingThreshold * 100)}%</small>
            </label>
          </div>
        )}
      </section>}

      <section className="apps-playback-health__section">
        <div className="apps-playback-health__section-heading">
          <div>
            <strong>{simple ? 'Screens to watch for' : 'Continuously monitored saved patterns'}</strong>
            <p>
              {simple
                ? 'Select the saved blockers that should continue to be handled after startup.'
                : 'Select any prompts or controls that should be checked whenever the health screenshot is already being taken. Their recorded responses run at most once until they clear.'}
            </p>
          </div>
          {visualRules.length > 0 && (
            <button
              type="button"
              onClick={() => update(
                'continuousRuleIds',
                config.continuousRuleIds?.length === visualRules.length
                  ? []
                  : visualRules.map((rule) => rule.id)
              )}
            >
              {config.continuousRuleIds?.length === visualRules.length ? 'Clear all' : 'Select all'}
            </button>
          )}
        </div>
        <div className="apps-playback-health__patterns">
          {visualRules.map((rule) => (
            <label key={rule.id}>
              <input
                type="checkbox"
                checked={config.continuousRuleIds?.includes(rule.id) || false}
                onChange={() => toggleContinuousRule(rule.id)}
              />
              {rule.detectors.find((entry) => entry.type === 'visual_template')
                ?.template?.templateDataUrl && (
                <img
                  src={rule.detectors.find((entry) => entry.type === 'visual_template')
                    .template.templateDataUrl}
                  alt=""
                />
              )}
              <span>
                <strong>{rule.name}</strong>
                <small>
                  {rule.response?.steps?.length
                    ? `${rule.response.steps.length} recorded response step(s)`
                    : 'Detection only'}
                </small>
              </span>
            </label>
          ))}
          {!visualRules.length && <small>No saved blockers with responses are available yet.</small>}
        </div>
        {simple && (
          <button type="button" onClick={onAddBlocker} disabled={disabled || Boolean(busy)}>
            Capture another blocker
          </button>
        )}
      </section>

      <div className="apps-playback-health__timing">
        {!simple && <label>
          <span>First probe after</span>
          <span><input type="number" min="10" max="300" value={config.initialDelayMs / 1000}
            onChange={(event) => update('initialDelayMs', Number(event.target.value) * 1000)} /> sec</span>
        </label>}
        <label>
          <span>{simple ? 'Check every' : 'Healthy probe interval'}</span>
          <span><input type="number" min="15" max="300" value={config.healthyProbeIntervalMs / 1000}
            onChange={(event) => update('healthyProbeIntervalMs', Number(event.target.value) * 1000)} /> sec</span>
        </label>
        {!simple && <label>
          <span>Confirmation interval</span>
          <span><input type="number" min="1.5" max="15" step="0.5"
            value={config.confirmationIntervalMs / 1000}
            onChange={(event) => update('confirmationIntervalMs', Number(event.target.value) * 1000)} /> sec</span>
        </label>}
        {!simple && <label>
          <span>Buffering matches required</span>
          <select value={config.requiredMatches}
            onChange={(event) => update('requiredMatches', Number(event.target.value))}>
            <option value="2">2 matches</option>
            <option value="3">3 matches</option>
          </select>
        </label>}
        {!simple && <label>
          <span>Screenshot budget</span>
          <span><input type="number" min="6" max="120" value={config.screenshotBudgetPerHour}
            onChange={(event) => update('screenshotBudgetPerHour', Number(event.target.value))} /> / hour</span>
        </label>}
      </div>

      {!simple && <label>
        <span>Buffering recovery response</span>
        <select
          value={config.recoveryRuleId || ''}
          onChange={(event) => {
            const recoveryRuleId = event.target.value;
            setConfig((current) => ({
              ...current,
              recoveryRuleId,
              recoveryEnabled: recoveryRuleId ? current.recoveryEnabled : false,
            }));
          }}
        >
          <option value="">Detection only</option>
          {recoveryRules.map((rule) => (
            <option key={rule.id} value={rule.id}>{rule.name}</option>
          ))}
        </select>
      </label>}
      {!simple && <label className="apps-playback-health__recovery">
        <input
          type="checkbox"
          checked={config.recoveryEnabled}
          onChange={(event) => update('recoveryEnabled', event.target.checked)}
          disabled={!config.recoveryRuleId}
        />
        <span>
          <strong>Enable trained buffering recovery</strong>
          <small>Leave this off until buffering detection-only tests are reliable.</small>
        </span>
      </label>}

      {latestRun && (
        <div className={`apps-playback-health__status ${latestRun.status}`}>
          <strong>{latestRun.status.replace(/_/g, ' ')}</strong>
          <span>
            {latestRun.result?.telemetryChecks || 0} telemetry checks
            {' '}· {latestRun.result?.screenshotRequests || 0} shared screenshots
            {' '}· {latestRun.result?.incidents?.length || 0} buffering incidents
            {' '}· {latestRun.result?.continuousOccurrences?.length || 0} pattern responses
          </span>
          {latestRun.result?.lastProbe?.buffering && (
            <small>
              Last buffering score:
              {' '}{Math.round((latestRun.result.lastProbe.buffering.confidence || 0) * 100)}%
            </small>
          )}
          {latestRun.result?.lastProbe?.patterns?.length > 0 && (
            <small>
              Last shared probe checked {latestRun.result.lastProbe.patterns.length} saved pattern(s).
            </small>
          )}
          {latestRun.result?.pressureSkips > 0 && (
            <small>{latestRun.result.pressureSkips} probes deferred under device pressure.</small>
          )}
          {latestRun.error && <small>Error: {latestRun.error}</small>}
        </div>
      )}

      <div className="apps-playback-health__actions">
        <button
          type="button"
          className="primary"
          onClick={handleSave}
          disabled={disabled || Boolean(busy) || (config.enabled && !hasMonitor)}
        >
          {busy === 'save' ? 'Saving…' : 'Save Continuous Monitoring'}
        </button>
      </div>
    </div>
  );
}

export default PlaybackHealthPanel;
